import { useEffect, useState } from 'react';
import get from 'lodash/get';
import { showErrorToast, showSuccessToast, Toast } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import EditAddressForm from '../../components/EditAddressForm';
import EditAddressSuggestion from '../../components/EditAddressSuggestion';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import {
  addressMessages,
  addressSuggestionStatus,
  countryNameCodes,
} from '../../constants/address';
import useUserShippingAddress from '../../hooks/useUserShippingAddress';
import { starTimer } from '../../services/newMember';
import { updateUserAddress } from '../../services/user';
import { URL_SUPPORT } from '../../utils/constants/urls';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import { clearUserCache } from '../MyAccount/services/api/account';
import sussesImg from './images/confirmation-image.jpg';
import './styles.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ReactivateThanks = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suggestionModal, setSuggestionModal] = useState({
    isOpen: true,
    subtitle: { firstLine: '', secondLine: '' },
  });
  const [address, setAddress] = useState({});
  const shippingAddress = useUserShippingAddress();
  const { userInfo } = useUserContext();
  const { chatbotAppliedLab: showChatbot } = useFlags();

  useEffect(() => {
    if (shippingAddress) {
      setAddress({
        countryCode: get(countryNameCodes, get(shippingAddress, 'country')),
        primaryLine: get(shippingAddress, 'address1'),
        secondaryLine: get(shippingAddress, 'address2'),
        city: get(shippingAddress, 'city'),
        state: get(shippingAddress, 'region'),
        zip: get(shippingAddress, 'code'),
      });
    }
  }, [shippingAddress]);

  const openSuccessToast = () => {
    showSuccessToast(
      'Primary Address Updated',
      'Your primary shipping address changes have been successfully saved.',
    );
  };

  const openErrorToast = () => {
    showErrorToast(
      'Primary Address Update Failed',
      'Your primary shipping address was not updated. Please try again.',
    );
  };

  const handleSubmit = async (response) => {
    const { modalType, submitted } = response;

    if (modalType === addressSuggestionStatus.VERIFIED) {
      const { success } = await updateUserAddress(submitted);
      if (success) {
        openSuccessToast();
      } else {
        openErrorToast();
      }
      clearUserCache();
    } else if (
      modalType === addressSuggestionStatus.NO_SUGGESTION ||
      modalType === addressSuggestionStatus.SUGGESTION_AVAILABLE
    ) {
      setSuggestionModal({
        isOpen: true,
        subtitle: addressMessages.SUBTITLE_NO_OPTIONS,
      });
    } else {
      console.warn('Address suggestion status invalid');
    }

    setAddress(submitted);
    setIsModalOpen(false);
  };

  const handleCloseSuggestion = () => {
    setIsModalOpen(true);
    setSuggestionModal({ isOpen: false });
  };

  const handleAcceptSuggestion = async () => {
    const { success } = await updateUserAddress(address);

    if (success) {
      openSuccessToast();
    } else {
      openErrorToast();
    }
    setAddress(address);
    clearUserCache();
    setIsModalOpen(false);
    setSuggestionModal({ isOpen: false });
  };

  const handleContinue = () => {
    starTimer()
      .then((response) => {
        if (response.ok) {
          redirectToBaseSite();
        } else {
          console.error('Timer is not started', response);
        }
      })

      .catch((error) => console.error(error));
  };

  return (
    <>
      <Layout isNMF useMemorized={false} hideChatWidget={!showChatbot}>
        {isModalOpen && (
          <Modal title="Edit Your Address" onClose={() => setIsModalOpen(false)}>
            <EditAddressForm
              onCancel={() => setIsModalOpen(false)}
              onSubmit={handleSubmit}
              address={address}
            />
          </Modal>
        )}
        {suggestionModal.isOpen && (
          <Modal
            title="Oops! Please Review"
            subtitle={suggestionModal.subtitle}
            onClose={() => setSuggestionModal((prevState) => ({ ...prevState, isOpen: false }))}
          >
            <EditAddressSuggestion
              address={address}
              onClose={handleCloseSuggestion}
              onAccept={handleAcceptSuggestion}
            />
          </Modal>
        )}
        <section className="reactivate-thanks">
          <div className="reactivate-thanks__title mobile">
            <h2>Success! We&apos;re Happy to Have You Back</h2>
          </div>
          <div className="reactivate-thanks__image">
            <img src={sussesImg} alt="susses" />
          </div>
          <div className="reactivate-thanks__address">
            <div className="reactivate-thanks__address__container">
              <div className="reactivate-thanks__title desktop">
                <h2>Success! We&apos;re Happy to Have You Back</h2>
              </div>
              <p className="reactivate-thanks__address__subtitle">
                Please verify your shipping address
              </p>
              <div className="reactivate-thanks__address__box">
                <div className="left">
                  <p className="name">
                    {get(userInfo, 'user.firstName')} {get(userInfo, 'user.lastName')}
                  </p>
                  <p className="address">{get(address, 'primaryLine')}</p>
                  {get(address, 'primaryLine') && (
                    <p className="address">{get(address, 'secondaryLine')}</p>
                  )}
                  <p className="city">
                    {get(address, 'city')}
                    {shippingAddress && ', '}
                    {get(address, 'state')} {get(address, 'zip')}
                  </p>
                </div>
                <div className="right">
                  {shippingAddress && (
                    <button type="button" onClick={() => setIsModalOpen(true)}>
                      Edit Address
                    </button>
                  )}
                </div>
              </div>
              <p className="reactivate-thanks__address__advice">
                Address changes will be applied toward your next shipment. If you need to update
                your address for a current shipment please contact FabFitFun Support{' '}
                <a href={URL_SUPPORT} target="_blank" rel="noreferrer">
                  here
                </a>{' '}
                if your shipment has not been processed yet.
              </p>
              <button
                type="button"
                className="reactivate-thanks__link fffBtnPrimary"
                onClick={handleContinue}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </section>
      </Layout>
      <Toast />
    </>
  );
};

export default ReactivateThanks;
